import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, 
  MessageSquare, 
  Users, 
  Clock, 
  ArrowLeft,
  Search,
  Filter,
  RefreshCw,
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
  ChevronDown,
  ChevronUp,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TwilioDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [calls, setCalls] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedConversation, setExpandedConversation] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, logsRes, templatesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/webhook/twilio/admin/stats?days=30`),
        axios.get(`${BACKEND_URL}/api/webhook/twilio/logs?limit=100`),
        axios.get(`${BACKEND_URL}/api/webhook/twilio/templates`)
      ]);
      
      setStats(statsRes.data);
      setLogs(logsRes.data.logs || []);
      setTemplates(templatesRes.data.templates || []);
      
      // Separate calls and SMS
      const callLogs = (logsRes.data.logs || []).filter(l => l.type === 'voice');
      const smsLogs = (logsRes.data.logs || []).filter(l => l.type === 'sms');
      setCalls(callLogs);
      
      // Group SMS into conversations
      const convMap = {};
      smsLogs.forEach(sms => {
        const key = sms.direction === 'incoming' ? sms.from_number : sms.to_number;
        if (!convMap[key]) {
          convMap[key] = {
            phone_number: key,
            customer_id: sms.customer_id,
            messages: [],
            last_message_at: sms.created_at
          };
        }
        convMap[key].messages.push(sms);
      });
      setConversations(Object.values(convMap));
      
    } catch (error) {
      console.error('Error fetching Twilio data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const filteredLogs = logs.filter(log => {
    if (filterType !== 'all' && log.type !== filterType) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        log.from_number?.toLowerCase().includes(query) ||
        log.to_number?.toLowerCase().includes(query) ||
        log.body?.toLowerCase().includes(query) ||
        log.transcript?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/clients')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-lg">Twilio Dashboard</span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-slate-700 text-white hover:bg-slate-800"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-900 border border-slate-800 rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <PhoneCall className="w-5 h-5 text-blue-400" />
              </div>
              <span className="text-slate-400">Total Calls</span>
            </div>
            <p className="text-3xl font-bold">{stats?.calls?.total || 0}</p>
            <p className="text-sm text-slate-500 mt-1">
              {stats?.calls?.recent || 0} in last 30 days
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-slate-900 border border-slate-800 rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-green-400" />
              </div>
              <span className="text-slate-400">Total SMS</span>
            </div>
            <p className="text-3xl font-bold">{stats?.sms?.total || 0}</p>
            <p className="text-sm text-slate-500 mt-1">
              {stats?.sms?.recent || 0} in last 30 days
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-900 border border-slate-800 rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-400" />
              </div>
              <span className="text-slate-400">Unique Contacts</span>
            </div>
            <p className="text-3xl font-bold">
              {(stats?.calls?.unique_callers || 0) + (stats?.sms?.unique_texters || 0)}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              {stats?.calls?.unique_callers || 0} callers, {stats?.sms?.unique_texters || 0} texters
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-slate-900 border border-slate-800 rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <span className="text-slate-400">Avg Call Duration</span>
            </div>
            <p className="text-3xl font-bold">
              {formatDuration(Math.round(stats?.duration?.avg_duration || 0))}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Total: {formatDuration(stats?.duration?.total_duration || 0)}
            </p>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-800 pb-4">
          {['overview', 'calls', 'sms', 'templates'].map((tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab(tab)}
              className={activeTab === tab 
                ? 'bg-sky-500 text-white' 
                : 'text-slate-400 hover:text-white'
              }
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Search and Filter */}
            <div className="flex gap-4 items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by phone number or message..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-900 border-slate-700 text-white"
                />
              </div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white"
              >
                <option value="all">All Types</option>
                <option value="voice">Calls Only</option>
                <option value="sms">SMS Only</option>
              </select>
            </div>

            {/* Activity Log */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-800">
                <h3 className="font-semibold">Recent Activity</h3>
              </div>
              <div className="divide-y divide-slate-800 max-h-[600px] overflow-y-auto">
                {filteredLogs.length === 0 ? (
                  <div className="p-8 text-center text-slate-400">
                    No activity found
                  </div>
                ) : (
                  filteredLogs.map((log, idx) => (
                    <div key={idx} className="px-6 py-4 hover:bg-slate-800/50 transition-colors">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          log.type === 'voice' ? 'bg-blue-500/20' : 'bg-green-500/20'
                        }`}>
                          {log.type === 'voice' ? (
                            <PhoneCall className={`w-5 h-5 ${log.direction === 'incoming' ? 'text-blue-400' : 'text-blue-300'}`} />
                          ) : (
                            <MessageSquare className={`w-5 h-5 ${log.direction === 'incoming' ? 'text-green-400' : 'text-green-300'}`} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">
                              {log.direction === 'incoming' ? log.from_number : log.to_number}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              log.direction === 'incoming' 
                                ? 'bg-blue-500/20 text-blue-400' 
                                : 'bg-purple-500/20 text-purple-400'
                            }`}>
                              {log.direction === 'incoming' ? 'Incoming' : 'Outgoing'}
                            </span>
                            {log.status && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                log.status === 'completed' 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-slate-500/20 text-slate-400'
                              }`}>
                                {log.status}
                              </span>
                            )}
                          </div>
                          {log.type === 'sms' && log.body && (
                            <p className="text-sm text-slate-300 truncate">{log.body}</p>
                          )}
                          {log.type === 'voice' && log.transcript && (
                            <p className="text-sm text-slate-300 truncate">"{log.transcript}"</p>
                          )}
                          {log.ai_response && (
                            <p className="text-sm text-sky-400 truncate mt-1">AI: {log.ai_response}</p>
                          )}
                          <p className="text-xs text-slate-500 mt-1">{formatDate(log.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Calls Tab */}
        {activeTab === 'calls' && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="font-semibold">Call History</h3>
              <span className="text-sm text-slate-400">{calls.length} calls</span>
            </div>
            <div className="divide-y divide-slate-800 max-h-[600px] overflow-y-auto">
              {calls.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <PhoneCall className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No calls recorded yet</p>
                </div>
              ) : (
                calls.map((call, idx) => (
                  <div key={idx} className="px-6 py-4 hover:bg-slate-800/50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <PhoneIncoming className="w-5 h-5 text-blue-400" />
                        <span className="font-medium">{call.from_number}</span>
                      </div>
                      <span className={`text-sm px-3 py-1 rounded-full ${
                        call.status === 'completed' 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-slate-500/20 text-slate-400'
                      }`}>
                        {call.status || 'in_progress'}
                      </span>
                    </div>
                    {call.transcript && (
                      <p className="text-sm text-slate-300 mb-2">"{call.transcript}"</p>
                    )}
                    {call.ai_response && (
                      <p className="text-sm text-sky-400 mb-2">AI: {call.ai_response}</p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span>{formatDate(call.created_at)}</span>
                      {call.duration && <span>Duration: {formatDuration(parseInt(call.duration))}</span>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* SMS Tab */}
        {activeTab === 'sms' && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="font-semibold">SMS Conversations</h3>
              <span className="text-sm text-slate-400">{conversations.length} conversations</span>
            </div>
            <div className="divide-y divide-slate-800 max-h-[600px] overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No SMS conversations yet</p>
                </div>
              ) : (
                conversations.map((conv, idx) => (
                  <div key={idx} className="hover:bg-slate-800/50 transition-colors">
                    <button
                      className="w-full px-6 py-4 text-left"
                      onClick={() => setExpandedConversation(
                        expandedConversation === conv.phone_number ? null : conv.phone_number
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                            <MessageSquare className="w-5 h-5 text-green-400" />
                          </div>
                          <div>
                            <p className="font-medium">{conv.phone_number}</p>
                            <p className="text-sm text-slate-400">
                              {conv.messages.length} messages
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-slate-500">
                            {formatDate(conv.last_message_at)}
                          </span>
                          {expandedConversation === conv.phone_number ? (
                            <ChevronUp className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                      </div>
                    </button>
                    
                    {expandedConversation === conv.phone_number && (
                      <div className="px-6 pb-4 space-y-3 bg-slate-800/30">
                        {conv.messages.slice(0, 10).map((msg, msgIdx) => (
                          <div
                            key={msgIdx}
                            className={`flex ${msg.direction === 'outgoing' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[80%] rounded-lg p-3 ${
                              msg.direction === 'outgoing'
                                ? 'bg-sky-500/20 text-sky-100'
                                : 'bg-slate-700 text-slate-200'
                            }`}>
                              <p className="text-sm">{msg.body}</p>
                              <p className="text-xs text-slate-400 mt-1">
                                {formatDate(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        ))}
                        {conv.messages.length > 10 && (
                          <p className="text-center text-sm text-slate-400">
                            +{conv.messages.length - 10} more messages
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="grid md:grid-cols-2 gap-4">
            {templates.map((template, idx) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-slate-900 border border-slate-800 rounded-xl p-6"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-purple-400" />
                  </div>
                  <h3 className="font-semibold">{template.name}</h3>
                </div>
                <p className="text-sm text-slate-300 bg-slate-800/50 rounded-lg p-3 font-mono">
                  {template.text}
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  Variables: {(template.text.match(/\{[^}]+\}/g) || []).join(', ') || 'None'}
                </p>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
